import styled from "styled-components"
import React, { FC, useContext } from "react"
import Helpers from "../../../../utils/Helpers"
import { HR, Spacer } from "../../atoms/Atoms"
import { Container, H3 } from "@carrotfertility/carotene"
import { Body, BodyLargeAlt } from "../../atoms/Bodies"
import { ArrowLink } from "../../atoms/links/ArrowLink"
import { getSupportCenterLink, SupportCenterLinks } from "../../../../utils/SupportCenterLinks"
import { CSD_CONFIGURATION, useUserDeductible } from "../../../context/user/DeductibleContext"
import L10n from "../../../../lib/carrot-l10n"
import { useSelector } from "react-redux"
import { getCurrencySymbol } from "../../../../reducers/companyInfo"
import { useReimbursementStatus } from "components/context/reimbursement/ReimbursementStatusContext"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

type CSDRowProps = {
  name: string
  value: string
}

type CSDHeaderProps = {
  name: string
}

const CSDTableParent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  margin-top: 8px;
  width: 100%;
  background: #faf9f7;
  border-radius: 8px;
`

const CSDTableChild = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  width: 100%;
  margin: 0px 10px;
`

const CSDHeaderParent = styled.div`
  left: 0px;
  top: 0px;
  width: 100%;
  margin: 2px 7px;
`

const CSDRowParent = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  margin: 1px 7px 2px 7px;
  padding: 1px 0;
  border-top: 1px solid #cccccc;
  @media (max-width: 525px) {
    height: 100%;
  }
`

const CSDRowChild = styled.div`
  left: 0px;
  top: 41px;
  font-family: Gotham;
  font-style: normal;
  width: 50%;
  font-weight: normal;
  line-height: 150%;
  color: #000000;
  @media (max-width: 525px) {
    height: 100%;
    margin-top: 1px;
    margin-right: 5px;
  }
`

const CSDRowValue = styled.div`
  display: flex;
  align-items: flex-start;
  width: 50%;
  line-height: 150%;
  margin: 0 5px;
  @media (max-width: 525px) {
    height: 100%;
    margin: 0 5px;
  }
`

const CSDHeader: FC<CSDHeaderProps> = ({ name }) => (
  <CSDHeaderParent>
    <H3>{name}</H3>
  </CSDHeaderParent>
)

const CSDRow: FC<CSDRowProps> = ({ name, value }) => (
  <CSDRowParent>
    <CSDRowChild>
      <Body color={"black-80"}>{name}</Body>
    </CSDRowChild>
    <CSDRowValue>
      <BodyLargeAlt color={"black-80"}>{value}</BodyLargeAlt>
    </CSDRowValue>
  </CSDRowParent>
)

const CSDTable: FC = () => {
  const deductibleStatus = useReimbursementStatus()
  const startDate = Helpers.formatDate(deductibleStatus.planStartDate)
  const endDate = Helpers.formatDate(deductibleStatus.planEndDate)

  const externalCreditCap = Math.max(
    0,
    deductibleStatus.deductibleAmount - deductibleStatus.carrotAmountAppliedToDeductible
  )
  const cappedExternalCredit = Math.min(externalCreditCap, deductibleStatus.externalAmountAppliedToDeductible)
  const cappedTotalApplied = Math.min(deductibleStatus.deductibleAmount, deductibleStatus.amountAppliedToDeductible)

  return (
    <div>
      <Spacer height={2.0} />
      <HR color={"black-10"} />
      <Spacer height={2.0} />

      <H3>{l`Your Carrot deductible`}</H3>
      <Body color="black-80">
        {l`For certain eligible expenses, you’ll need to pay an annual deductible before you can receive reimbursements through Carrot. `}
      </Body>

      <Spacer height={1.0} />
      <H3>{l`Your Carrot deductible status:`}</H3>

      <CSDTableParent>
        <CSDTableChild>
          <CSDHeader
            name={l(`Plan year: {{startDate}} — {{endDate}}`, {
              startDate,
              endDate
            })}
          />
          <CSDRow
            name={l`Annual deductible`}
            value={Helpers.convertToCurrencyString(
              deductibleStatus.deductibleAmount,
              deductibleStatus.currencySymbol,
              0
            )}
          />
          <CSDRow
            name={l`Credit from insurance deductible`}
            value={Helpers.convertToCurrencyString(cappedExternalCredit, deductibleStatus.currencySymbol, 0)}
          />
          <CSDRow
            name={l`What’s been paid so far`}
            value={Helpers.convertToCurrencyString(cappedTotalApplied, deductibleStatus.currencySymbol, 0)}
          />
          <CSDRow
            name={l`What’s left to pay `}
            value={Helpers.convertToCurrencyString(
              deductibleStatus.deductibleRemaining,
              deductibleStatus.currencySymbol,
              0
            )}
          />
        </CSDTableChild>
      </CSDTableParent>

      <Spacer height={1.0} />
      <ArrowLink
        to={getSupportCenterLink(SupportCenterLinks.WHY_HAVE_DEDUCTIBLE_MANAGED_BY_CARROT)}
      >{l`Learn more about the Carrot deductible`}</ArrowLink>
    </div>
  )
}

// Updated CSD Table for Partner CSD Milestone 4 found below *Dark Release*

function useMemberCSDTableSetup() {
  const {
    deductible: { deductibleSnapshot: memberDeductibleSnapshot }
  } = useUserDeductible()
  const startDateMember = Helpers.formatDate(memberDeductibleSnapshot.planStartDate)
  const endDateMember = Helpers.formatDate(memberDeductibleSnapshot.planEndDate)
  const externalCreditCapMember = Math.max(
    0,
    memberDeductibleSnapshot.totalAmount - memberDeductibleSnapshot.carrotAmountAppliedToDeductible
  )
  const cappedExternalCreditMember = Math.min(
    externalCreditCapMember,
    memberDeductibleSnapshot.externalAmountAppliedToDeductible
  )
  const cappedTotalAppliedMember = Math.min(
    memberDeductibleSnapshot.totalAmount,
    memberDeductibleSnapshot.amountAppliedToDeductible
  )
  return {
    memberDeductibleSnapshot,
    startDateMember,
    endDateMember,
    cappedExternalCreditMember,
    cappedTotalAppliedMember
  }
}

function usePartnerCSDTableSetup() {
  const {
    partnerDeductible: { deductibleSnapshot: partnerDeductibleSnapshot }
  } = useUserDeductible()
  const startDatePartner = Helpers.formatDate(partnerDeductibleSnapshot.planStartDate)
  const endDatePartner = Helpers.formatDate(partnerDeductibleSnapshot.planEndDate)
  const externalCreditCapPartner = Math.max(
    0,
    partnerDeductibleSnapshot.totalAmount - partnerDeductibleSnapshot.amountAppliedToDeductible
  )
  const cappedExternalCreditPartner = Math.min(
    externalCreditCapPartner,
    partnerDeductibleSnapshot.externalAmountAppliedToDeductible
  )
  const cappedTotalAppliedPartner = Math.min(
    partnerDeductibleSnapshot.totalAmount,
    partnerDeductibleSnapshot.amountAppliedToDeductible
  )
  return {
    partnerDeductibleSnapshot,
    startDatePartner,
    endDatePartner,
    cappedExternalCreditPartner,
    cappedTotalAppliedPartner
  }
}

function CSDTableMember(): JSX.Element {
  const currencySymbol = useSelector(getCurrencySymbol)
  const {
    memberDeductibleSnapshot,
    startDateMember,
    endDateMember,
    cappedExternalCreditMember,
    cappedTotalAppliedMember
  } = useMemberCSDTableSetup()
  return (
    <Container padding="none" stack="large">
      <H3>{l`Your deductible status:`}</H3>
      <CSDTableParent>
        <CSDTableChild>
          <CSDHeader
            name={l(`Plan year: {{startDateMember}} — {{endDateMember}}`, {
              startDateMember,
              endDateMember
            })}
          />
          <CSDRow
            name={l`Annual deductible`}
            value={Helpers.convertToCurrencyString(memberDeductibleSnapshot.totalAmount, currencySymbol, 0)}
          />
          <CSDRow
            name={l`Credit from insurance deductible`}
            value={Helpers.convertToCurrencyString(cappedExternalCreditMember, currencySymbol, 0)}
          />
          <CSDRow
            name={l`What’s been paid so far`}
            value={Helpers.convertToCurrencyString(cappedTotalAppliedMember, currencySymbol, 0)}
          />
          <CSDRow
            name={l`What’s left to pay `}
            value={Helpers.convertToCurrencyString(memberDeductibleSnapshot.amountRemaining, currencySymbol, 0)}
          />
        </CSDTableChild>
      </CSDTableParent>
    </Container>
  )
}

function CSDTablePartner(): JSX.Element {
  const currencySymbol = useSelector(getCurrencySymbol)
  const {
    partnerDeductibleSnapshot,
    startDatePartner,
    endDatePartner,
    cappedExternalCreditPartner,
    cappedTotalAppliedPartner
  } = usePartnerCSDTableSetup()

  return (
    <Container padding="none" stack="large">
      <H3>{l`Your partner's deductible status:`}</H3>
      <CSDTableParent>
        <CSDTableChild>
          <CSDHeader
            name={l(`Plan year: {{startDatePartner}} — {{endDatePartner}}`, {
              startDatePartner,
              endDatePartner
            })}
          />
          <CSDRow
            name={l`Annual deductible`}
            value={Helpers.convertToCurrencyString(partnerDeductibleSnapshot.totalAmount, currencySymbol, 0)}
          />
          <CSDRow
            name={l`Credit from insurance deductible`}
            value={Helpers.convertToCurrencyString(cappedExternalCreditPartner, currencySymbol, 0)}
          />
          <CSDRow
            name={l`What’s been paid so far`}
            value={Helpers.convertToCurrencyString(cappedTotalAppliedPartner, currencySymbol, 0)}
          />
          <CSDRow
            name={l`What’s left to pay `}
            value={Helpers.convertToCurrencyString(partnerDeductibleSnapshot.amountRemaining, currencySymbol, 0)}
          />
        </CSDTableChild>
      </CSDTableParent>
    </Container>
  )
}

function CSDTableContainer(): JSX.Element {
  const { csdConfiguration, hasCSD } = useUserDeductible()
  const hasLegacyCSD = hasCSD && csdConfiguration === null
  const shouldShowMemberTable =
    [
      CSD_CONFIGURATION.MEMBER_AND_PARTNER_SEPARATE,
      CSD_CONFIGURATION.MEMBER_AND_PARTNER_COMBINED,
      CSD_CONFIGURATION.MEMBER_ONLY
    ].includes(csdConfiguration) || hasLegacyCSD
  const shouldShowPartnerTable = [
    CSD_CONFIGURATION.MEMBER_AND_PARTNER_SEPARATE,
    CSD_CONFIGURATION.PARTNER_ONLY
  ].includes(csdConfiguration)
  return (
    <div>
      {shouldShowMemberTable && <CSDTableMember />}
      {shouldShowPartnerTable && <CSDTablePartner />}
      <ArrowLink
        to={getSupportCenterLink(SupportCenterLinks.WHY_HAVE_DEDUCTIBLE_MANAGED_BY_CARROT)}
      >{l`Learn more about the Carrot deductible`}</ArrowLink>
    </div>
  )
}

export { CSDTable, CSDTableContainer }
